import {
  DashboardScopeKind,
  IDashboardScope,
  TeamSegment
} from 'src/types/graphql'
import { DashboardRole, NavKey } from './navItems'

export type DashboardNav = {
  desktop: NavKey[]
  mobileTabs: NavKey[]
  more: NavKey[]
}

/** Fixed desktop order — `resolveDashboardNav` only decides presence. */
const HOST_ORDER: NavKey[] = [
  'hoy',
  'rentas',
  'interesados',
  'espacios',
  'tratos',
  'mensajes',
  'inventario',
  'pagos',
  'cuenta',
  'admin'
]
/** Mobile bar = Hoy + first three PRESENT items in this rank. */
const MOBILE_RANK: NavKey[] = [
  'rentas',
  'interesados',
  'mensajes',
  'tratos',
  'espacios',
  'inventario',
  'pagos',
  'cuenta',
  'admin'
]
/**
 * Platform admin on "Todas" (Jules 2026-09-27): staff manage every host's and
 * renter's account, so the client accounts (Cuentas = all teams), their rents,
 * listings and deals lead; the Admin panel tops the lower group.
 */
const ADMIN_ORDER: NavKey[] = [
  'hoy',
  'cuentas',
  'rentas',
  'espacios',
  'tratos',
  'mensajes',
  'interesados',
  'admin',
  'inventario',
  'pagos'
]
const ADMIN_MOBILE_RANK: NavKey[] = ADMIN_ORDER.filter((k) => k !== 'hoy')
const MOBILE_TABS = 3
const RENTER_NAV: NavKey[] = ['hoy', 'rentas', 'mensajes', 'cuenta']

/** Used when dashboardContext failed: a SpotMe Now host with listings. */
export const FALLBACK_HOST_SCOPE: IDashboardScope = {
  id: 'fallback',
  kind: DashboardScopeKind.User,
  name: null,
  iconUrl: null,
  segment: TeamSegment.None,
  isPartner: false,
  isOnDemand: false,
  isLogistics: false,
  liveListings: 1,
  anyListings: 1,
  hasLeads: false,
  hasOnPlatformRents: false,
  hasClosedDeals: false,
  inventoryPending: 0,
  viewerCanEdit: true
}

export const PLATFORM_SCOPE_ID = 'platform'

/**
 * A platform admin on "Todas las cuentas" sees platform-wide data (every
 * reservation, lead and deal), so the nav must offer every item. The server
 * deliberately returns only the admin's PERSONAL scope (admins sit on ~50
 * customer teams), which would otherwise compute a brand-new-host nav.
 */
export const PLATFORM_SCOPE: IDashboardScope = {
  id: PLATFORM_SCOPE_ID,
  kind: DashboardScopeKind.User,
  name: null,
  iconUrl: null,
  segment: TeamSegment.None,
  isPartner: true,
  isOnDemand: true,
  isLogistics: false,
  liveListings: 1,
  anyListings: 1,
  hasLeads: true,
  hasOnPlatformRents: true,
  hasClosedDeals: true,
  inventoryPending: 0,
  viewerCanEdit: true
}

/** Mirrors the server's isOffPlatformSegment: Traditional | Pro only. */
export const isOffPlatform = (s: IDashboardScope): boolean =>
  s.segment === TeamSegment.Traditional || s.segment === TeamSegment.Pro

const isNowLike = (s: IDashboardScope): boolean =>
  s.segment === TeamSegment.None || s.segment === TeamSegment.Custom

/**
 * Does this scope actually host? A TEAM scope alone is not enough: renter
 * company teams come back from the same membership list and carry no host
 * fact — treating them as hosts sent B2B renters to host Hoy after login.
 */
export const scopeHostsSomething = (s: IDashboardScope): boolean =>
  s.anyListings > 0 ||
  isOffPlatform(s) ||
  s.segment === TeamSegment.Custom ||
  s.isPartner ||
  s.isOnDemand ||
  s.hasLeads ||
  s.hasClosedDeals ||
  s.hasOnPlatformRents

/** Presence of each item for ONE scope (spec §4 table). */
export function itemsForScope(s: IDashboardScope): Set<NavKey> {
  const keys = new Set<NavKey>(['hoy', 'mensajes', 'cuenta'])
  // Pagos = on-platform money (Stripe payouts + monthly CFDI), so it follows
  // Rentas exactly; Traditional/Pro partners are off-platform and Stripe-exempt.
  if ((isNowLike(s) && s.liveListings > 0) || s.hasOnPlatformRents) {
    keys.add('rentas')
    keys.add('pagos')
  }
  if (s.hasLeads || isOffPlatform(s) || s.isOnDemand) keys.add('interesados')
  if (!(s.isOnDemand && s.liveListings === 0)) keys.add('espacios')
  if (isOffPlatform(s) || s.isOnDemand || s.hasClosedDeals) keys.add('tratos')
  if ((isNowLike(s) && s.liveListings > 0) || s.inventoryPending > 0)
    keys.add('inventario')
  return keys
}

/**
 * PURE — which scopes the nav is computed from. A failed or empty host
 * context degrades to the Now shape (never a blank sidebar); while loading
 * it returns nothing so the shell shows skeleton rows instead of flashing
 * the wrong items. Renters never receive the host fallback.
 */
export function resolveNavScopes(input: {
  scopesInView: IDashboardScope[]
  loading: boolean
  failed: boolean
  role: DashboardRole
  /** Admin with no team/host selected — never waits on the query. */
  platformWide?: boolean
}): IDashboardScope[] {
  if (input.role === 'rent') return input.scopesInView
  if (input.platformWide) return [PLATFORM_SCOPE]
  if (input.failed) return [FALLBACK_HOST_SCOPE]
  if (input.loading) return []
  return input.scopesInView.length ? input.scopesInView : [FALLBACK_HOST_SCOPE]
}

export function resolveDashboardNav(input: {
  scopes: IDashboardScope[]
  role: DashboardRole
  isAdmin: boolean
}): DashboardNav {
  if (input.role === 'rent') {
    return { desktop: RENTER_NAV, mobileTabs: RENTER_NAV, more: [] }
  }
  const present = new Set<NavKey>()
  for (const scope of input.scopes) {
    for (const key of itemsForScope(scope)) present.add(key)
  }
  if (input.isAdmin) present.add('admin')
  const platformWide =
    input.isAdmin && input.scopes.some((s) => s.id === PLATFORM_SCOPE_ID)
  if (platformWide) {
    // "Cuenta" would open the teams list anyway — name it for what it is.
    present.delete('cuenta')
    present.add('cuentas')
  }
  const order = platformWide ? ADMIN_ORDER : HOST_ORDER
  const rank = platformWide ? ADMIN_MOBILE_RANK : MOBILE_RANK
  const desktop = order.filter((k) => present.has(k))
  const ranked = rank.filter((k) => present.has(k))
  const mobileTabs: NavKey[] = ['hoy', ...ranked.slice(0, MOBILE_TABS)]
  const more = desktop.filter((k) => !mobileTabs.includes(k))
  return { desktop, mobileTabs, more }
}
